import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { PageShell } from "../../components/layout/PageShell";
import { SectionTitle } from "../GRCManagerDashboard/grcDashboardWidgets";
import { PagFilterBar } from "./components/PagFilterBar";
import { PagReviewTable } from "./components/PagReviewTable";
import { PolicyInsightsGraph } from "./components/PolicyInsightsGraph";
import { NetworkGraph } from "./components/NetworkGraph";
import { fetchPagNetworkGraphData } from "../../../api/frameworksApi";
import {
  buildPagNetworkModel,
  buildPagTree,
  type PagApplication,
  type PagCycle,
  type PagFilters,
} from "./data/pagTypes";

type GraphTab = "tree" | "network";

const GRAPH_TABS: { id: GraphTab; label: string }[] = [
  { id: "tree", label: "Policy Insights" },
  { id: "network", label: "Network Graph" },
];

/** Hides the inactive graph panel while keeping it laid out and measurable.
 *
 *  opacity-0, NOT `invisible`. visibility:hidden is inherited but a
 *  descendant can override it with visibility:visible, and something inside
 *  the React Flow tree does exactly that -- which is how a strip of Policy
 *  Insights labels kept painting along the bottom of the Network Graph
 *  while its panel was supposedly hidden. Opacity cannot be opted back into
 *  by a child, and unlike display:none it still leaves a real box for both
 *  libraries to measure themselves against. */
const PANEL_HIDDEN = "pointer-events-none opacity-0 absolute inset-x-0 top-0";

/* ══════════════════════════════════════════════════════════════════════
   PRIVILEGE ACCESS GOVERNANCE — top-level dashboard for privileged
   access posture.

   Every panel is live now: the main filter API fills the filter bar, the
   network-graph endpoint feeds BOTH graph tabs from one call, and the
   Details table pages the current-cycle list server-side.

   The Application filter reaches all three as `appCodes`, where an empty
   list means every application. Department / Division are selectable but
   do not narrow anything yet — neither request body carries them.
   ══════════════════════════════════════════════════════════════════════ */

/* Every filter opens unset. The filter bar fills `applications` with the
   first application as soon as the filter tree lands, and only then does
   anything fetch — see `filtersReady` below. An empty application list
   still means "all", which is what Clear falls back to. */
const INITIAL_FILTERS: PagFilters = {
  departments: [],
  divisions: [],
  applications: [],
};

export function PrivilegeAccessGovernance() {
  const [filters, setFilters] = useState<PagFilters>(INITIAL_FILTERS);
  const [cycle, setCycle] = useState<PagCycle>("current");
  const [graphTab, setGraphTab] = useState<GraphTab>("tree");

  /* Held false until the filter bar has resolved its tree and applied the
     default application. Every request below waits on it, so the first one
     out carries that appCode rather than an empty list. */
  const [filtersReady, setFiltersReady] = useState(false);

  const [graphApps, setGraphApps] = useState<PagApplication[]>([]);
  const [graphLoading, setGraphLoading] = useState(true);

  /* The filter already holds appCodes, so it travels to every request as
     it stands. An empty list is "every application", which is what each
     endpoint already means by it.

     Joined for the dependency: the array is rebuilt on every render of
     the filter state, and comparing by identity would refire the fetch
     even when the selection has not changed. */
  const appCodes = filters.applications;
  const appCodesKey = appCodes.join(",");

  /* One call feeds both tabs. The response carries the whole access tree
     for the selected applications, so expanding a node in the network
     view is a lookup rather than another request. */
  useEffect(() => {
    if (!filtersReady) return;
    let cancelled = false;
    setGraphLoading(true);

    fetchPagNetworkGraphData(appCodes)
      .then((res) => {
        if (!cancelled) setGraphApps(res?.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setGraphApps([]);
      })
      .finally(() => {
        if (!cancelled) setGraphLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appCodesKey, filtersReady]);

  /* Both tabs draw every application the filter selected. Policy Insights
     used to take graphApps[0] only, so picking four applications drew the
     first and silently dropped the rest. */
  const treeNodes = useMemo(() => buildPagTree(graphApps), [graphApps]);
  const networkModel = useMemo(() => buildPagNetworkModel(graphApps), [graphApps]);

  return (
    <PageShell className="space-y-4">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Privilege Access Governance
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Trace privileged access from application to account, identity, and
            granted policy
          </p>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────── */}
      <PagFilterBar
        applied={filters}
        onApply={setFilters}
        onClear={() => setFilters(INITIAL_FILTERS)}
        onReady={() => setFiltersReady(true)}
      />

      <motion.div
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        {/* ── Graph tabs ───────────────────────────────────────────────
            Both views are kept mounted and toggled with `hidden` rather
            than unmounted: vis-network re-runs its physics stabilisation
            on every mount, and React Flow re-plays its intro flight, so
            unmounting would restart both animations on each tab switch
            and throw away the user's zoom and expansion state. */}
        <div className="rounded-xl border border-border bg-card p-5 pt-3">
          <div className="mb-3 flex items-center gap-5 border-b border-border">
            {GRAPH_TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setGraphTab(t.id)}
                className={`border-b-2 pb-1.5 text-sm transition-colors ${
                  graphTab === t.id
                    ? "border-indigo-500 font-semibold text-foreground"
                    : "border-transparent font-medium text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* The inactive panel is hidden with `invisible` + `absolute`, NOT
              `hidden`/display:none. display:none collapses the panel to 0x0,
              and React Flow measures its canvas from the container: switching
              tabs while it was still initialising made it divide by that zero
              and emit hundreds of NaN geometry errors. Staying absolutely
              positioned keeps a real box at any moment. */}
          <div className="relative">
            <div className={graphTab === "tree" ? "" : PANEL_HIDDEN}>
              <PolicyInsightsGraph treeNodes={treeNodes} loading={graphLoading} />
            </div>
            <div className={graphTab === "network" ? "" : PANEL_HIDDEN}>
              <NetworkGraph model={networkModel} loading={graphLoading} />
            </div>
          </div>
        </div>

        {/* ── Details table ────────────────────────────────────────── */}
        <PagReviewTable
          appCodes={appCodes}
          ready={filtersReady}
          cycle={cycle}
          onCycleChange={setCycle}
        />
      </motion.div>
    </PageShell>
  );
}
